<script setup>
const props = defineProps({
  open: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: 'Confirm action',
  },
  message: {
    type: String,
    default: '',
  },
  details: {
    type: Array,
    default: () => [],
  },
  confirmLabel: {
    type: String,
    default: 'Confirm',
  },
  cancelLabel: {
    type: String,
    default: 'Cancel',
  },
  tone: {
    type: String,
    default: 'neutral',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  loadingLabel: {
    type: String,
    default: 'Working...',
  },
})

const emit = defineEmits(['close', 'confirm'])

const onBackdropClose = () => {
  if (props.loading) return
  emit('close')
}
</script>

<template>
  <div v-if="open" class="modal-backdrop admin-action-backdrop" @click.self="onBackdropClose">
    <div class="modal-panel admin-action-panel">
      <div class="dialog-header">
        <span :class="['dialog-tone', `dialog-tone-${tone}`]" aria-hidden="true"></span>
        <div>
          <h3>{{ title }}</h3>
          <p v-if="message" class="dialog-message">{{ message }}</p>
        </div>
      </div>

      <ul v-if="details.length" class="dialog-details">
        <li v-for="detail in details" :key="detail">{{ detail }}</li>
      </ul>

      <div class="dialog-actions">
        <button type="button" class="btn btn-secondary" :disabled="loading" @click="emit('close')">
          {{ cancelLabel }}
        </button>
        <button type="button" :class="['btn', `btn-${tone}`]" :disabled="loading" @click="emit('confirm')">
          {{ loading ? loadingLabel : confirmLabel }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(17, 24, 39, 0.45);
  display: grid;
  place-items: center;
  z-index: 1000;
}

.modal-panel {
  width: min(560px, 92vw);
  background: #fff;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  padding: 1rem;
  display: grid;
  gap: 0.9rem;
}

.dialog-header {
  display: grid;
  grid-template-columns: auto 1fr;
  gap: 0.75rem;
  align-items: flex-start;
}

.dialog-header h3,
.dialog-message {
  margin: 0;
}

.dialog-message {
  margin-top: 0.25rem;
  color: #374151;
}

.dialog-tone {
  width: 0.85rem;
  height: 0.85rem;
  border-radius: 9999px;
  margin-top: 0.3rem;
}

.dialog-tone-danger {
  background: #dc2626;
}

.dialog-tone-success {
  background: #059669;
}

.dialog-tone-warning {
  background: #d97706;
}

.dialog-tone-neutral {
  background: #6b7280;
}

.dialog-details {
  margin: 0;
  padding-left: 1.2rem;
  color: #4b5563;
  display: grid;
  gap: 0.35rem;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.btn {
  border: none;
  border-radius: 6px;
  color: #fff;
  padding: 0.5rem 0.85rem;
}

.btn-secondary {
  background: #4b5563;
}

.btn-danger {
  background: #b91c1c;
}

.btn-success {
  background: #047857;
}

.btn-warning {
  background: #b45309;
}

.btn-neutral {
  background: #2563eb;
}

@media (max-width: 640px) {
  .dialog-actions {
    flex-direction: column-reverse;
  }

  .dialog-actions .btn {
    width: 100%;
  }
}
</style>
